import { stringify } from '@angular/compiler/src/util';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTable, MatTableDataSource } from '@angular/material/table';

import {
    CustomPanelGetListData,
    CustomPanelGetListInput,
    CustomPanelGetListOutput,
} from 'projects/sen-app/src/app/core/common/api/CustomPanelGetList';
import { PanelGetOutput } from 'projects/sen-app/src/app/core/common/api/PanelGet';
import {
    PanelRemoveInput,
    PanelRemoveOutput,
} from 'projects/sen-app/src/app/core/common/api/PanelRemove';
import { PanelSaveInput } from 'projects/sen-app/src/app/core/common/api/PanelSave';
import { ProjectGetData } from 'projects/sen-app/src/app/core/common/api/ProjectGet';
import { SC_Input } from 'projects/sen-app/src/app/core/common/input/SC_Input';
import { Observable, Subscription } from 'rxjs';

import { Store } from '@ngrx/store';

import { ModulProp } from '../../../../../common/panel/ModulProp';
import { GlobalFunctionService } from '../../../../../common/services/global-function.service';
import { GlobalService } from '../../../../../common/services/global.service';
import { GlobalVariableService } from '../../../../../common/variables/global-variable';
import { TranslateService } from '../../../../../common/variables/translate-service';

@Component({
    selector: 'sen-app-own-panel',
    templateUrl: './own-panel.component.html',
    styleUrls: ['./own-panel.component.scss'],
})
export class OwnPanelComponent implements OnInit {
    @ViewChild(MatTable) customPanelList: MatTable<any>;
    @Output() formStatus = new EventEmitter();
    @Output() sPanel = new EventEmitter();
    @Input() validateForm: boolean;

    selectedProject$: Observable<any>;
    selectedProject: ProjectGetData;
    unsubscribeSelectedProject: Subscription;
    displayedColumns: string[] = ['manufacturer', 'name', 'action'];
    dataSource = new MatTableDataSource();
    selectedPanelDetails: boolean = false;
    cellType = [
        {
            label: this.translate.getLng('MOD_MONO'),
            value: ModulProp.TYPE_MONO,
        },
        {
            label: this.translate.getLng('MOD_POLY'),
            value: ModulProp.TYPE_POLY,
        },
        {
            label: this.translate.getLng('MOD_THIN'),
            value: ModulProp.TYPE_THIN,
        },
    ];
    ownPanelNames: string[] = [];
    addCustomPanelAction: boolean = false;
    sPanelID: number;
    formData: any = {};
    public form: FormGroup;
    globalFPanelUnsubscribe: Subscription;
    projectPanel: boolean;
    projectPanelDisable: boolean = true;
    maskPattern: string;
    sol30Note: any;
    sol30Modal: boolean = false;

    constructor(
        public global: GlobalVariableService,
        private store: Store<any>,
        private _snackBar: MatSnackBar,
        public globalF: GlobalFunctionService,
        public globalService: GlobalService,
        public formGroup: FormBuilder,
        public translate: TranslateService,
    ) {
        this.selectedProject$ = this.store.select((state) => {
            return state.appData.selectedProject;
        });
        this.unsubscribeSelectedProject = this.selectedProject$.subscribe(
            (data) => {
                this.selectedProject = data;
            },
        );
        if (this.global.sLanguage === this.global.deString) {
            this.maskPattern = '0*,00000';
        } else {
            this.maskPattern = '0*.00000';
        }
        this.form = this.formGroup.group({
            id: [0],
            manufacturer: [
                '',
                [
                    Validators.required,
                    Validators.minLength(this.global.minStringLength),
                    Validators.maxLength(this.global.maxStringLength),
                ],
            ],
            name: [
                '',
                [
                    Validators.required,
                    Validators.minLength(this.global.minStringLength),
                    Validators.maxLength(this.global.maxStringLength),
                ],
            ],
            width: [
                null,
                [
                    Validators.required,
                    Validators.min(this.global.PANEL_SIZE_MIN),
                    Validators.max(this.global.PANEL_SIZE_MAX),
                ],
            ],
            height: [
                null,
                [
                    Validators.required,
                    Validators.min(this.global.PANEL_SIZE_MIN),
                    Validators.max(this.global.PANEL_SIZE_MAX),
                ],
            ],
            type: [null, Validators.required],
            thickness: [
                SC_Input.iMountSys > 10 ? 30 : null,
                [
                    Validators.required,
                    Validators.min(this.global.PANEL_THICK_MIN),
                    Validators.max(this.global.PANEL_THICK_MAX),
                ],
            ],
            Icoeff: [null, Validators.required],
            Vcoeff: [null, Validators.required],
            Ipm: [null, Validators.required],
            Vpm: [null, Validators.required],
            Voc: [null, Validators.required],
            Vsys: [null, Validators.required],
            Pdc: [null, Validators.required],
            Isc: [null, Validators.required],
            Ireverse: [null, Validators.required],
            landscape: [false, Validators.required],
        });
        this.form.valueChanges.subscribe((data: PanelSaveInput) => {
            this.formData = data;
            this.emitValues();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.validateForm && !changes.validateForm.isFirstChange()) {
            this.form.markAllAsTouched();
        }
    }

    ngOnInit() {
        this.getCustomPanelList();
        this.form.get('type')?.setValue(ModulProp.TYPE_MONO);
        if (SC_Input.iMountSys > 10) {
            this.form.get('thickness')?.setValue(30);
        }
        if (!SC_Input.sPanel.senItemID && SC_Input.sPanel.id) {
            this.setProjectPanel(SC_Input.sPanel.id);
            this.setValues(SC_Input.sPanel);
            setTimeout(() => {
                this.formStatus.emit(false);
            }, 1000);
        }
        setTimeout(() => {
            this.sol30Note = document.querySelector('.sol30-note');
        }, 100);
    }

    ngOnDestroy() {
        SC_Input.sPlanDetails.projectPanel = this.projectPanel;
        if (this.globalFPanelUnsubscribe)
            this.globalFPanelUnsubscribe.unsubscribe();
        if (this.unsubscribeSelectedProject)
            this.unsubscribeSelectedProject.unsubscribe();
    }

    setUserPanelSelected() {
        SC_Input.userOwnPanelSelected = this.projectPanel;
    }

    setProjectPanel(id: number) {
        if (SC_Input.userOwnPanelSelected) {
            this.projectPanel = true;
            this.projectPanelDisable = false;
        } else if (
            id == this.selectedProject.panelID ||
            !this.selectedProject.roofPlans.length ||
            (this.selectedProject.roofPlans.length == 1 && SC_Input.sPlan)
        ) {
            this.projectPanel = true;
            this.projectPanelDisable = true;
        } else {
            this.projectPanel = false;
            this.projectPanelDisable = false;
        }
    }

    checkVoltage() {
        let voc = this.form.get('Voc');
        let vpm = this.form.get('Vpm');
        if (
            parseInt(voc!.value) < parseInt(vpm!.value) ||
            !voc!.value ||
            !vpm!.value
        ) {
            voc!.setErrors({ voltageError: true });
            vpm!.setErrors({ voltageError: true });
        } else {
            voc!.setErrors(null);
            vpm!.setErrors(null);
        }
    }

    checkCurrent() {
        let isc = this.form.get('Isc');
        let ipm = this.form.get('Ipm');
        if (
            parseInt(isc!.value) < parseInt(ipm!.value) ||
            !isc!.value ||
            !ipm!.value
        ) {
            isc!.setErrors({ currentError: true });
            ipm!.setErrors({ currentError: true });
        } else {
            isc!.setErrors(null);
            ipm!.setErrors(null);
        }
    }

    get formControls() {
        return this.form.controls;
    }

    clearForm() {
        this.form.reset();
        this.form.get('type')?.setValue(ModulProp.TYPE_MONO);
        if (SC_Input.iMountSys > 10) {
            this.form.get('thickness')?.setValue(30);
        }
    }

    sol30ModalClose() {
        this.sol30Modal = false;
    }

    emitValues() {
        if (!this.form.invalid && this.form.pristine) {
            setTimeout(() => {
                this.formStatus.emit(true);
            }, 0);
            this.sPanel.emit(this.getParsedValues());
            return;
        }
        setTimeout(() => {
            this.formStatus.emit(false);
        }, 0);
    }

    getParsedValues(): PanelSaveInput {
        return {
            id: this.formData.id,
            name: this.formData.name,
            shortName: '',
            manufacturer: this.formData.manufacturer,
            Icoeff:
                this.global.sLanguage === this.global.deString
                    ? parseFloat(
                          stringify(this.formData.Icoeff).replace(',', '.'),
                      )
                    : parseFloat(this.formData.Icoeff),
            Vcoeff:
                this.global.sLanguage === this.global.deString
                    ? parseFloat(
                          stringify(this.formData.Vcoeff).replace(',', '.'),
                      )
                    : parseFloat(this.formData.Vcoeff) * -1,
            Ipm:
                this.global.sLanguage === this.global.deString
                    ? parseFloat(stringify(this.formData.Ipm).replace(',', '.'))
                    : parseFloat(this.formData.Ipm),
            Vpm:
                this.global.sLanguage === this.global.deString
                    ? parseFloat(stringify(this.formData.Vpm).replace(',', '.'))
                    : parseFloat(this.formData.Vpm),
            Voc:
                this.global.sLanguage === this.global.deString
                    ? parseFloat(stringify(this.formData.Voc).replace(',', '.'))
                    : parseFloat(this.formData.Voc),
            Vsys: parseFloat(this.formData.Vsys),
            Pdc: parseFloat(this.formData.Pdc),
            Isc:
                this.global.sLanguage === this.global.deString
                    ? parseFloat(stringify(this.formData.Isc).replace(',', '.'))
                    : parseFloat(this.formData.Isc),
            Ireverse: parseFloat(this.formData.Ireverse),
            type: parseInt(this.formData.type),
            width: parseInt(this.formData.width),
            height: parseInt(this.formData.height),
            thickness: parseInt(this.formData.thickness),
            landscape: this.formData.landscape,
        };
    }

    setValues(data: any) {
        if (data.thickness > 30 && SC_Input.iMountSys > 10) {
            this.sol30Modal = true;
            setTimeout(() => {
                this.sol30Modal = false;
            }, 8000);
        } else {
            this.form.get('manufacturer')?.setValue(data.manufacturer);
            this.form.get('name')?.setValue(data.name);
            this.form.get('width')?.setValue(data.width);
            this.form.get('height')?.setValue(data.height);
            this.form.get('thickness')?.setValue(data.thickness);
            this.form.get('type')?.setValue(data.type);
            if (this.global.sLanguage === this.global.deString) {
                this.form
                    .get('Icoeff')
                    ?.setValue(stringify(data.Icoeff).replace('.', ','));
                this.form
                    .get('Vcoeff')
                    ?.setValue(stringify(data.Vcoeff).replace('.', ','));
                this.form
                    .get('Ipm')
                    ?.setValue(stringify(data.Ipm).replace('.', ','));
                this.form
                    .get('Vpm')
                    ?.setValue(stringify(data.Vpm).replace('.', ','));
                this.form
                    .get('Voc')
                    ?.setValue(stringify(data.Voc).replace('.', ','));
                this.form
                    .get('Isc')
                    ?.setValue(stringify(data.Isc).replace('.', ','));
            } else {
                this.form.get('Icoeff')?.setValue(data.Icoeff);
                this.form.get('Vcoeff')?.setValue(data.Vcoeff);
                this.form.get('Ipm')?.setValue(data.Ipm);
                this.form.get('Vpm')?.setValue(data.Vpm);
                this.form.get('Isc')?.setValue(data.Isc);
                this.form.get('Voc')?.setValue(data.Voc);
            }
            this.form.get('Ireverse')?.setValue(data.Ireverse);
            this.form.get('Pdc')?.setValue(data.Pdc);
            this.form.get('Vsys')?.setValue(data.Vsys);
            this.form.get('id')?.setValue(data.id);
            this.form.get('landscape')?.setValue(data.landscape);
            this.form.markAllAsTouched();
        }
    }

    getCustomPanelList() {
        const qParam: CustomPanelGetListInput = {
            cmd: this.global.CustomPanelGetList,
        };
        this.globalService
            .getData(qParam)
            .subscribe((data: CustomPanelGetListOutput) => {
                try {
                    if (data.ok.data.panels) {
                        this.ownPanelNames = [];
                        this.dataSource = new MatTableDataSource(
                            data.ok.data.panels,
                        );
                        this.customPanelList.renderRows();
                        // to get id for the newly added item
                        if (this.addCustomPanelAction) {
                            this.formData.id =
                                data.ok.data.panels[
                                    data.ok.data.panels.length - 1
                                ].id;
                            this.setProjectPanel(this.formData.id);
                            this.addCustomPanelAction = false;
                        }
                        this.emitValues();
                        data.ok.data.panels.forEach(
                            (p: CustomPanelGetListData) => {
                                this.ownPanelNames.push(p.name.toLowerCase());
                            },
                        );
                    }
                } catch {}
            });
    }

    getPanelDetails(panel: any) {
        if (!this.formData.id || panel.id != this.formData.id) {
            this.globalFPanelUnsubscribe = this.globalF
                .getPanelDetail(panel.id)
                .subscribe((data: PanelGetOutput) => {
                    try {
                        if (data.ok.data) {
                            this.selectedPanelDetails = true;
                            data.ok.data.id = panel.id;
                            this.setValues(data.ok.data);
                            this.setProjectPanel(panel.id);
                            this.form.markAsPristine();
                            this.emitValues();
                        }
                    } catch {}
                });
        }
    }

    addCustomPanel() {
        this.form.markAllAsTouched();
        if (this.form.invalid || this.form.pristine) {
            return;
        }
        const qParam: CustomPanelGetListInput = {
            cmd: this.global.PanelSave,
        };
        let option: PanelSaveInput = this.getParsedValues();
        this.globalService.postData(qParam, option).subscribe((data: any) => {
            if (data.ok?.data) {
                if (!this.formData.id) {
                    this.addCustomPanelAction = true;
                }
                this.form.markAsPristine();
                this.getCustomPanelList();
            } else if (data.error?.code == this.global.nameCollision) {
                this._snackBar.open(data.error?.message || '');
            }
        });
    }

    removePanel(event: any, panel: any) {
        event.stopPropagation();
        const qParam: PanelRemoveInput = {
            cmd: this.global.PanelRemove,
            id: panel.id,
        };
        this.globalService
            .getData(qParam)
            .subscribe((data: PanelRemoveOutput) => {
                try {
                    if (data.ok) {
                        this.getCustomPanelList();
                    }
                } catch {}
            });
    }
}
